import {
  useEffect, useRef, useState, 
} from 'react';
import {
  Link, useLocation, useNavigate, 
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import logo from '/logo.svg';
import profile from '/profile.svg';
import burger from '/burgerIcon.svg';

import Button from 'components/Button';
import useUser from 'api/hooks/useUser';
import { persistWebViewLoadingInformation } from 'utils/helpers';
import SecureStorage from 'utils/localStorage';

import cancelSvg from '/cancelIcon.svg';
import svgProfile from '/svgProfile.svg';
import svgLogout from '/svgLogout.svg';

import './index.css';
import logOutMutation from 'api/mutations/logOutMutation';

import listenForOutsideClicks from './listenForOutsideClicks';

const Header = () => {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const { data: user, isPending } = useUser();
  const logOut = logOutMutation();
  const [burgerMenu, setBurgerMenuVisibility] = useState<boolean>(false);
  const reactNativeWebView = (window as any)?.ReactNativeWebView;

  const menuRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  // Scroll detection state
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(
    listenForOutsideClicks(listening, setListening, menuRef, setIsOpen),
  );

  // Scroll detection effect
  useEffect(() => {
    // Skip scroll detection on /croptimizer route
    if (pathname === '/croptimizer') {
      setIsHeaderVisible(true);

      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < 10) {
        // Always show header when at the top
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 10) {
        // Scrolling down and past 100px - hide header
        setIsHeaderVisible(false);
        setBurgerMenuVisibility(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, pathname]);

  useEffect(() => {
    setBurgerMenuVisibility(false);

    return () => setBurgerMenuVisibility(false);
  }, [pathname, hash]);

  const shouldHideHeaderForSpecificUrl =
    pathname === '/auth/login-distributor' ||
    pathname === '/auth/login-mfa' ||
    pathname === '/auth/confirm-mfa' ||
    pathname === '/auth/set-phone-number' ||
    pathname === '/auth/confirm-phone-number' ||
    pathname === '/auth/login-all' ||
    pathname === '/auth/login-set-password' ||
    pathname === '/auth/login-expired' ||
    pathname === '/auth/login-omya';

  const handleLogOut = async () => {
    reactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'logout',
      }),
    );
    logOut.mutate(undefined, {
      onSuccess: () => {
        SecureStorage.clear();
        persistWebViewLoadingInformation();
        window.location.href = '/auth/login-all';
        setIsOpen(false);
      },
      onError: () => {
        SecureStorage.clear();
        persistWebViewLoadingInformation();
        window.location.href = '/auth/login-all';
        setIsOpen(false);
      },
    });
  };

  if (shouldHideHeaderForSpecificUrl) {
    return null;
  }

  return (
    <div
      className={`header-container ${
        isHeaderVisible ? 'header-visible' : 'header-hidden'
      } flex items-center justify-between px-[105px] py-[14px] md:px-0 md: py-[8px] md:w-full md:m-0 border-b border-primitiveGrey`}
    >
      <div className="flex items-center justify-between">
        <div
          onClick={() => navigate('/')}
          className="mr-[90px] ml-0 md:ml-[20px] cursor-pointer"
        >
          <img src={logo} alt="logo" />
        </div>
        <div
          onClick={() => navigate('/')}
          className="text-secondaryText text-base mr-[40px] cursor-pointer md:hidden"
        >
          {t('home')}
        </div>
        <div
          onClick={() => navigate('/help')}
          className="text-secondaryText text-base cursor-pointer md:hidden"
        >
          {t('help')}
        </div>
      </div>
      <div className="flex items-center justify-center">
        {user?.role?.id !== 3 &&
          user?.role?.id !== 4 &&
          user?.role?.id !== 5 &&
          !isPending && (
          <a target="_blank" href="https://omya.yellow.team" rel="noreferrer">
            <Button className="md:hidden" size="sm" btnType="secondary">
              {t('adminPanel')}
            </Button>
          </a>
        )}

        <div ref={menuRef} className={isOpen ? 'm-menu -active' : 'm-menu '}>
          <div
            onClick={toggle}
            className="bg-primary rounded-full ml-[24px] cursor-pointer md:hidden relative w-[40px] h-[40px] flex items-center justify-center"
          >
            <img className="m-[10px]" src={profile} alt="profile" />
          </div>
          <ul className="m-menu__list">
            <li className="m-menu__item">
              {localStorage.getItem('netStatus') !== 'offline' && (
                <div className="m-menu__link">
                  <div
                    onClick={() => {
                      navigate('/profile');
                      setIsOpen(false);
                    }}
                    className="cursor-pointer flex items-center border-b p-4"
                  >
                    <img src={svgProfile} alt="" />
                    <div className="ml-4 cursor-pointer">{t('profile')}</div>
                  </div>
                </div>
              )}
              <div className="m-menu__link">
                <div
                  onClick={handleLogOut}
                  className="cursor-pointer flex items-center p-4"
                >
                  <img src={svgLogout} alt="" />
                  <div className="ml-4">{t('logout')}</div>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div className="md:flex hidden">
          <div
            onClick={() => setBurgerMenuVisibility(!burgerMenu)}
            className="bg-primary flex items-center justify-center border border-primary rounded-full mr-5 w-[40px] h-[40px] cursor-pointer"
          >
            <img src={burger} alt="menu" />
          </div>

          {burgerMenu && isHeaderVisible && (
            <div className="md:flex md:fixed md:right-0 md:top-0 md:bottom-0 md:flex-col md:w-52 md:bg-grey md:min-h-dvh md:z-50">
              <div className="flex justify-end my-3">
                <div
                  onClick={() => setBurgerMenuVisibility(!burgerMenu)}
                  className="bg-grey flex items-center justify-center border border-secondaryText rounded-full mr-6 w-[40px] h-[40px] cursor-pointer"
                >
                  <img src={cancelSvg} alt="close" />
                </div>
              </div>
              <Link
                id="home"
                className={`cursor-pointer border-b-primitiveGrey border-b py-2 font-medium md:pl-6 md:bg-grey ${
                  pathname === '/' && 'text-primary'
                }`}
                to="/"
              >
                {t('home')}
              </Link>
              <Link
                id="about"
                className={`cursor-pointer border-b-primitiveGrey border-b py-2 font-medium md:pl-6 md:bg-grey ${
                  pathname === '/help' && 'text-primary'
                }`}
                to="/help"
              >
                {t('help')}
              </Link>
              {user?.role?.id !== 3 &&
                user?.role?.id !== 4 &&
                user?.role?.id !== 5 && (
                <a
                  id="about"
                  className={
                    'cursor-pointer border-b-primitiveGrey border-b py-2 font-medium'
                  }
                  target="_blank"
                  href="https://omya.yellow.team"
                  rel="noreferrer"
                >
                  {t('adminPanel')}
                </a>
              )}
              {localStorage.getItem('netStatus') !== 'offline' && (
                <Link
                  id="contact"
                  className={`cursor-pointer border-b-primitiveGrey border-b py-2 font-medium md:pl-6 md:bg-grey ${
                    pathname === '/profile' && 'text-primary'
                  }`}
                  to="/profile"
                >
                  {t('profile')}
                </Link>
              )}
              <div
                className={
                  'cursor-pointer border-b-primitiveGrey border-b py-2 font-medium md:pl-6 md:bg-grey'
                }
                onClick={handleLogOut}
              >
                {t('logout')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
